import React from "react";
import { Wallet, RefreshCw } from "lucide-react";
import { WalletPassesSkeleton } from "./skeletons";

/**
 * WalletPassesCard — right-rail summary of issued wallet passes.
 *
 * Two-column grid (Google / Apple), each tile showing the count of
 * active passes for that platform with a gradient-tinted icon.
 *
 * Props:
 *   googleCount        — number of active Google Wallet passes
 *   appleCount         — number of active Apple Wallet (.pkpass) passes
 *   onRegenerate       — handler for the bottom Regenerate passes button
 *   isRegenerating     — bool; disables the button + spins the icon
 *   googleEnabled      — render the Google tile (defaults to true)
 *   appleEnabled       — render the Apple tile (defaults to true)
 *
 * Testid preservation: keeps `regenerate-wallets-btn` on the Regenerate
 * button so the existing automation continues to find it.
 */

const WalletPassesCard = ({
  googleCount = 0,
  appleCount = 0,
  onRegenerate,
  isRegenerating = false,
  googleEnabled = true,
  appleEnabled = true,
  loading = false,
}) => {
  if (loading) return <WalletPassesSkeleton />;

  const totalActive = (googleEnabled ? googleCount : 0) + (appleEnabled ? appleCount : 0);

  if (!googleEnabled && !appleEnabled) return null;

  return (
    <div
      className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4"
      data-testid="wallet-passes-card"
    >
      <div className="flex items-center justify-between mb-3 gap-2">
        <h3 className="text-[13px] font-semibold text-zinc-100 whitespace-nowrap font-heading">
          Wallet Passes
        </h3>
        <span className="px-1.5 py-0.5 rounded text-[10px] bg-emerald-500/10 text-emerald-300 border border-emerald-500/25 font-mono whitespace-nowrap">
          {totalActive} ACTIVE
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2.5 mb-3">
        {googleEnabled && (
          <div className="relative rounded-lg border border-emerald-500/20 bg-gradient-to-br from-emerald-500/[0.06] via-zinc-900/40 to-zinc-900/40 p-3 overflow-hidden">
            <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-emerald-500/10 blur-2xl pointer-events-none" />
            <div className="relative">
              <div className="w-7 h-7 rounded-md bg-gradient-to-br from-emerald-400 to-green-600 flex items-center justify-center text-white text-[12px] font-bold mb-2 shadow-[0_0_12px_rgba(52,211,153,.35)]">
                G
              </div>
              <div className="text-[22px] font-bold text-zinc-50 leading-none font-heading">
                {googleCount}
              </div>
              <div className="text-[10.5px] text-zinc-500 mt-1 font-mono">
                Google Wallet
              </div>
            </div>
          </div>
        )}
        {appleEnabled && (
          <div className="relative rounded-lg border border-zinc-700/40 bg-gradient-to-br from-zinc-700/[0.10] via-zinc-900/40 to-zinc-900/40 p-3 overflow-hidden">
            <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-zinc-400/5 blur-2xl pointer-events-none" />
            <div className="relative">
              <div className="w-7 h-7 rounded-md bg-gradient-to-br from-zinc-300 to-zinc-500 flex items-center justify-center text-white text-[12px] font-bold mb-2 shadow-[0_0_12px_rgba(161,161,170,.35)]">

              </div>
              <div className="text-[22px] font-bold text-zinc-50 leading-none font-heading">
                {appleCount}
              </div>
              <div className="text-[10.5px] text-zinc-500 mt-1 font-mono">
                .pkpass files
              </div>
            </div>
          </div>
        )}
      </div>

      {onRegenerate && (
        <button
          type="button"
          onClick={onRegenerate}
          disabled={isRegenerating}
          data-testid="regenerate-wallets-btn"
          className="w-full flex items-center justify-center gap-2 h-9 rounded-lg bg-zinc-900/70 border border-zinc-800 hover:border-zinc-700 text-zinc-300 hover:text-zinc-100 text-[12px] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isRegenerating ? (
            <RefreshCw size={12} className="animate-spin" />
          ) : (
            <Wallet size={12} />
          )}
          {isRegenerating ? "Regenerating…" : "Regenerate passes"}
        </button>
      )}
    </div>
  );
};

export default WalletPassesCard;
